import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { Chatbot, chatbotApi } from '../services/api';

// Zustandstyp
interface ChatbotState {
    chatbots: Chatbot[];
    currentChatbot: Chatbot | null;
    isLoading: boolean;
    error: string | null;
}

// Aktionstypen
type ChatbotAction =
    | { type: 'FETCH_CHATBOTS_REQUEST' }
    | { type: 'FETCH_CHATBOTS_SUCCESS'; payload: Chatbot[] }
    | { type: 'FETCH_CHATBOTS_FAILURE'; payload: string }
    | { type: 'FETCH_CHATBOT_REQUEST' }
    | { type: 'FETCH_CHATBOT_SUCCESS'; payload: Chatbot }
    | { type: 'FETCH_CHATBOT_FAILURE'; payload: string }
    | { type: 'CREATE_CHATBOT_REQUEST' }
    | { type: 'CREATE_CHATBOT_SUCCESS'; payload: Chatbot }
    | { type: 'CREATE_CHATBOT_FAILURE'; payload: string }
    | { type: 'UPDATE_CHATBOT_REQUEST' }
    | { type: 'UPDATE_CHATBOT_SUCCESS'; payload: Chatbot }
    | { type: 'UPDATE_CHATBOT_FAILURE'; payload: string }
    | { type: 'DELETE_CHATBOT_REQUEST' }
    | { type: 'DELETE_CHATBOT_SUCCESS'; payload: string }
    | { type: 'DELETE_CHATBOT_FAILURE'; payload: string };

// Anfangszustand
const initialState: ChatbotState = {
    chatbots: [],
    currentChatbot: null,
    isLoading: false,
    error: null,
};

// Reducer-Funktion
const chatbotReducer = (state: ChatbotState, action: ChatbotAction): ChatbotState => {
    switch (action.type) {
        // Abrufen aller Chatbots
        case 'FETCH_CHATBOTS_REQUEST':
            return { ...state, isLoading: true, error: null };
        case 'FETCH_CHATBOTS_SUCCESS':
            return { ...state, isLoading: false, chatbots: action.payload };
        case 'FETCH_CHATBOTS_FAILURE':
            return { ...state, isLoading: false, error: action.payload };

        // Abrufen eines einzelnen Chatbots
        case 'FETCH_CHATBOT_REQUEST':
            return { ...state, isLoading: true, error: null };
        case 'FETCH_CHATBOT_SUCCESS':
            return { ...state, isLoading: false, currentChatbot: action.payload };
        case 'FETCH_CHATBOT_FAILURE':
            return { ...state, isLoading: false, error: action.payload };

        // Erstellen eines Chatbots
        case 'CREATE_CHATBOT_REQUEST':
            return { ...state, isLoading: true, error: null };
        case 'CREATE_CHATBOT_SUCCESS':
            return {
                ...state,
                isLoading: false,
                chatbots: [...state.chatbots, action.payload],
                currentChatbot: action.payload,
            };
        case 'CREATE_CHATBOT_FAILURE':
            return { ...state, isLoading: false, error: action.payload };

        // Aktualisieren eines Chatbots
        case 'UPDATE_CHATBOT_REQUEST':
            return { ...state, isLoading: true, error: null };
        case 'UPDATE_CHATBOT_SUCCESS':
            return {
                ...state,
                isLoading: false,
                chatbots: state.chatbots.map((chatbot) =>
                    chatbot.id === action.payload.id ? action.payload : chatbot
                ),
                currentChatbot: action.payload,
            };
        case 'UPDATE_CHATBOT_FAILURE':
            return { ...state, isLoading: false, error: action.payload };

        // Löschen eines Chatbots
        case 'DELETE_CHATBOT_REQUEST':
            return { ...state, isLoading: true, error: null };
        case 'DELETE_CHATBOT_SUCCESS':
            return {
                ...state,
                isLoading: false,
                chatbots: state.chatbots.filter((chatbot) => chatbot.id !== action.payload),
                currentChatbot: state.currentChatbot?.id === action.payload ? null : state.currentChatbot,
            };
        case 'DELETE_CHATBOT_FAILURE':
            return { ...state, isLoading: false, error: action.payload };

        default:
            return state;
    }
};

// Context erstellen
interface ChatbotContextType {
    state: ChatbotState;
    fetchChatbots: () => Promise<void>;
    fetchChatbot: (id: string) => Promise<void>;
    createChatbot: (data: Partial<Chatbot>) => Promise<void>;
    updateChatbot: (id: string, data: Partial<Chatbot>) => Promise<void>;
    deleteChatbot: (id: string) => Promise<void>;
}

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

// Provider-Komponente
export const ChatbotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(chatbotReducer, initialState);

    // Alle Chatbots abrufen
    const fetchChatbots = useCallback(async () => {
        dispatch({ type: 'FETCH_CHATBOTS_REQUEST' });
        try {
            const chatbots = await chatbotApi.getAllChatbots();
            dispatch({ type: 'FETCH_CHATBOTS_SUCCESS', payload: chatbots });
        } catch (error: any) {
            dispatch({ type: 'FETCH_CHATBOTS_FAILURE', payload: error.message });
        }
    }, []);

    // Einzelnen Chatbot abrufen
    const fetchChatbot = useCallback(async (id: string) => {
        dispatch({ type: 'FETCH_CHATBOT_REQUEST' });
        try {
            const chatbot = await chatbotApi.getChatbotById(id);
            dispatch({ type: 'FETCH_CHATBOT_SUCCESS', payload: chatbot });
        } catch (error: any) {
            dispatch({ type: 'FETCH_CHATBOT_FAILURE', payload: error.message });
        }
    }, []);

    // Neuen Chatbot erstellen
    const createChatbot = useCallback(async (data: Partial<Chatbot>) => {
        dispatch({ type: 'CREATE_CHATBOT_REQUEST' });
        try {
            if (!data.name) {
                throw new Error('Chatbot name is required');
            }
            const newChatbot = await chatbotApi.createChatbot({
                name: data.name, // hier ist name garantiert vorhanden
                description: data.description
            });
            dispatch({ type: 'CREATE_CHATBOT_SUCCESS', payload: newChatbot });
        } catch (error: any) {
            dispatch({ type: 'CREATE_CHATBOT_FAILURE', payload: error.message });
        }
    }, []);

    // Chatbot aktualisieren
    const updateChatbot = useCallback(async (id: string, data: Partial<Chatbot>) => {
        dispatch({ type: 'UPDATE_CHATBOT_REQUEST' });
        try {
            const updatedChatbot = await chatbotApi.updateChatbot(id, data);
            dispatch({ type: 'UPDATE_CHATBOT_SUCCESS', payload: updatedChatbot });
        } catch (error: any) {
            dispatch({ type: 'UPDATE_CHATBOT_FAILURE', payload: error.message });
        }
    }, []);

    // Chatbot löschen
    const deleteChatbot = useCallback(async (id: string) => {
        dispatch({ type: 'DELETE_CHATBOT_REQUEST' });
        try {
            await chatbotApi.deleteChatbot(id);
            dispatch({ type: 'DELETE_CHATBOT_SUCCESS', payload: id });
        } catch (error: any) {
            dispatch({ type: 'DELETE_CHATBOT_FAILURE', payload: error.message });
        }
    }, []);

    return (
        <ChatbotContext.Provider
            value={{
                state,
                fetchChatbots,
                fetchChatbot,
                createChatbot,
                updateChatbot,
                deleteChatbot,
            }}
        >
            {children}
        </ChatbotContext.Provider>
    );
};

// Custom Hook für den einfacheren Zugriff
export const useChatbot = () => {
    const context = useContext(ChatbotContext);
    if (context === undefined) {
        throw new Error('useChatbot must be used within a ChatbotProvider');
    }
    return context;
};